<template>
  <div class="map-toolbar" :class="{ 'toolbar-vertical': vertical }">
    <!-- 地图工具控制 -->
    <div class="toolbar-group">
      <el-tooltip content="重置视图" placement="left">
        <button class="toolbar-button" @click="resetView">
          <i class="el-icon-refresh-right"></i>
        </button>
      </el-tooltip>
      <el-tooltip content="定位工具" placement="left">
        <button 
          class="toolbar-button" 
          :class="{'active-tool': showLocationDialog}"
          @click="toggleLocationDialog">
          <i class="el-icon-position"></i>
        </button>
      </el-tooltip>
      <el-tooltip content="测距工具" placement="left">
        <button 
          class="toolbar-button" 
          :class="{'active-tool': activeTool === 'measure'}"
          @click="toggleTool('measure')">
          <i class="el-icon-ruler"></i>
        </button>
      </el-tooltip>
    </div>
    
    <!-- 图层控制 -->
    <div class="toolbar-group">
      <el-tooltip content="图层控制" placement="left">
        <button 
          class="toolbar-button" 
          :class="{'active-tool': showLayerControl}"
          @click="toggleLayerControl">
          <i class="el-icon-s-operation"></i>
        </button>
      </el-tooltip>
      <el-tooltip content="全屏显示" placement="left">
        <button class="toolbar-button" @click="toggleFullscreen">
          <i class="el-icon-full-screen"></i>
        </button>
      </el-tooltip>
    </div>
    
    <!-- 图层控制面板 (条件显示) -->
    <div class="layer-control-panel" v-if="showLayerControl">
      <h4 class="panel-title">图层控制</h4>
      <div class="layer-list">
        <div v-for="layer in layers" :key="layer.id" class="layer-item">
          <el-checkbox v-model="layer.visible" @change="(val) => updateLayerVisibility(layer.id, val)">
            {{ layer.name }}
          </el-checkbox>
        </div>
        <div v-if="layers.length === 0" class="no-layers">
          暂无可用图层
        </div>
      </div>
    </div>
    
    <!-- 定位工具对话框 -->
    <el-dialog
      title="坐标定位"
      :visible.sync="showLocationDialog"
      width="400px"
      :append-to-body="true"
      :close-on-click-modal="false">
      <div class="location-input-container">
        <el-form label-width="80px">
          <el-form-item label="输入格式:">
            <el-radio-group v-model="coordinateFormat" size="small">
              <el-radio label="decimal">十进制度数</el-radio>
              <el-radio label="dms">度分秒</el-radio>
            </el-radio-group>
          </el-form-item>
          
          <!-- 十进制度数输入 -->
          <template v-if="coordinateFormat === 'decimal'">
            <el-form-item label="纬度:">
              <el-input
                v-model="decimalCoords.lat"
                type="text"
                placeholder="如: 31.400"
                @keyup.enter.native="gotoLocation">
                <template slot="append">°</template>
              </el-input>
            </el-form-item>
            <el-form-item label="经度:">
              <el-input
                v-model="decimalCoords.lng"
                type="text"
                placeholder="如: 121.539"
                @keyup.enter.native="gotoLocation">
                <template slot="append">°</template>
              </el-input>
            </el-form-item>
          </template>
          
          <!-- 度分秒输入 -->
          <template v-else>
            <el-form-item label="纬度:">
              <div class="dms-input-group">
                <el-input v-model.number="dmsCoords.lat.degrees" type="number" placeholder="度" size="small">
                  <template slot="append">°</template>
                </el-input>
                <el-input v-model.number="dmsCoords.lat.minutes" type="number" placeholder="分" size="small" :max="59">
                  <template slot="append">'</template>
                </el-input>
                <el-input v-model.number="dmsCoords.lat.seconds" type="number" placeholder="秒" size="small" :max="59" step="0.1">
                  <template slot="append">"</template>
                </el-input>
                <el-select v-model="dmsCoords.lat.direction" size="small" style="width: 60px;">
                  <el-option label="N" value="N"></el-option>
                  <el-option label="S" value="S"></el-option>
                </el-select>
              </div>
            </el-form-item>
            <el-form-item label="经度:">
              <div class="dms-input-group">
                <el-input v-model.number="dmsCoords.lng.degrees" type="number" placeholder="度" size="small">
                  <template slot="append">°</template>
                </el-input>
                <el-input v-model.number="dmsCoords.lng.minutes" type="number" placeholder="分" size="small" :max="59">
                  <template slot="append">'</template>
                </el-input>
                <el-input v-model.number="dmsCoords.lng.seconds" type="number" placeholder="秒" size="small" :max="59" step="0.1">
                  <template slot="append">"</template>
                </el-input>
                <el-select v-model="dmsCoords.lng.direction" size="small" style="width: 60px;">
                  <el-option label="E" value="E"></el-option>
                  <el-option label="W" value="W"></el-option>
                </el-select>
              </div>
            </el-form-item>
          </template>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showLocationDialog = false">取消</el-button>
        <el-button type="primary" @click="gotoLocation">定位</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'MapToolbar',
  props: {
    // 是否垂直排列
    vertical: {
      type: Boolean,
      default: true
    },
    // 可用的地图图层
    availableLayers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 当前激活的工具
      activeTool: '',
      // 是否显示图层控制面板
      showLayerControl: false,
      // 图层列表（本地状态）
      layers: [],
      // 定位工具相关
      showLocationDialog: false,
      locationInput: '',
      coordinateFormat: 'decimal', // 'decimal' 或 'dms'
      decimalCoords: {
        lat: '',
        lng: ''
      },
      dmsCoords: {
        lat: {
          degrees: null,
          minutes: null,
          seconds: null,
          direction: 'N'
        },
        lng: {
          degrees: null,
          minutes: null,
          seconds: null,
          direction: 'E'
        }
      }
    };
  },
  created() {
    // 初始化图层数据
    this.initLayers();
  },
  methods: {
    // 初始化图层数据
    initLayers() {
      // 深拷贝以避免直接修改props
      this.layers = JSON.parse(JSON.stringify(this.availableLayers));
      
      // 如果没有提供图层，添加默认图层
      if (this.layers.length === 0) {
        this.layers = [
          { id: 'base', name: '底图', visible: true },
          { id: 'bridge', name: '桥梁标记', visible: true },
          { id: 'area', name: '区域显示', visible: true }
        ];
      }
    },
    
    // 重置地图视图
    resetView() {
      this.$emit('reset-view');
    },
    
    // 切换工具
    toggleTool(tool) {
      // 如果当前工具已激活，则取消激活
      if (this.activeTool === tool) {
        this.activeTool = '';
        this.$emit('tool-change', '');
      } else {
        this.activeTool = tool;
        this.$emit('tool-change', tool);
      }
    },
    
    // 切换图层控制面板显示状态
    toggleLayerControl() {
      this.showLayerControl = !this.showLayerControl;
    },
    
    // 切换全屏显示
    toggleFullscreen() {
      this.$emit('toggle-fullscreen');
    },
    
    // 更新图层可见性
    updateLayerVisibility(layerId, isVisible) {
      this.$emit('layer-visibility-change', { layerId, isVisible });
    },
    
    // 切换定位对话框显示状态
    toggleLocationDialog() {
      this.showLocationDialog = !this.showLocationDialog;
      if (this.showLocationDialog) {
        // 清空输入
        this.locationInput = '';
        this.decimalCoords.lat = '';
        this.decimalCoords.lng = '';
      }
    },
    
    // 解析坐标并跳转到指定位置
    gotoLocation() {
      try {
        let lat, lng;
        
        if (this.coordinateFormat === 'decimal') {
          // 十进制度数格式 - 使用普通文本输入，在此处进行验证
          const latStr = this.decimalCoords.lat;
          const lngStr = this.decimalCoords.lng;
          
          if (!latStr || !lngStr || latStr.trim() === '' || lngStr.trim() === '') {
            throw new Error('请输入完整的纬度和经度');
          }
          
          // 验证输入格式：只允许数字、小数点、负号
          const numberPattern = /^-?\d+(\.\d*)?$/;
          if (!numberPattern.test(latStr.trim())) {
            throw new Error('纬度格式不正确，请输入有效的数字（如：31.400）');
          }
          if (!numberPattern.test(lngStr.trim())) {
            throw new Error('经度格式不正确，请输入有效的数字（如：121.539）');
          }
          
          lat = parseFloat(latStr.trim());
          lng = parseFloat(lngStr.trim());
          
          if (isNaN(lat) || isNaN(lng)) {
            throw new Error('坐标必须是有效数字');
          }
        } else {
          // 度分秒格式
          lat = this.convertDMSToDecimal(this.dmsCoords.lat);
          lng = this.convertDMSToDecimal(this.dmsCoords.lng);
          
          if (lat === null || lng === null) {
            throw new Error('请输入完整的度分秒坐标');
          }
        }
        
        // 验证坐标范围
        if (lat < -90 || lat > 90) {
          throw new Error('纬度必须在-90到90之间');
        }
        
        if (lng < -180 || lng > 180) {
          throw new Error('经度必须在-180到180之间');
        }
        
        // 发送定位事件
        this.$emit('goto-location', { lat, lng });
        
        // 关闭对话框
        this.showLocationDialog = false;
        
        this.$message.success(`已定位到: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        
      } catch (error) {
        this.$message.error(`坐标解析失败: ${error.message}`);
      }
    },
    
    // 度分秒转十进制度数
    convertDMSToDecimal(dms) {
      const { degrees, minutes, seconds, direction } = dms;
      
      if (degrees === null || degrees === '' || 
          minutes === null || minutes === '' || 
          seconds === null || seconds === '') {
        return null;
      }
      
      const deg = parseFloat(degrees);
      const min = parseFloat(minutes);
      const sec = parseFloat(seconds);
      
      if (isNaN(deg) || isNaN(min) || isNaN(sec)) {
        throw new Error('度分秒必须是有效数字');
      }
      
      if (min >= 60 || sec >= 60) {
        throw new Error('分和秒的值必须小于60');
      }
      
      let decimal = deg + min / 60 + sec / 3600;
      
      // 根据方向调整符号
      if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
      }
      
      return decimal;
    }
  },
  watch: {
    // 监听外部图层变化
    availableLayers: {
      handler(newLayers) {
        if (newLayers && newLayers.length > 0) {
          this.layers = JSON.parse(JSON.stringify(newLayers));
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.map-toolbar {
  position: absolute;
  background-color: rgba(28, 47, 123, 0.85);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  padding: 0;
  display: flex;
  gap: 0;
  overflow: hidden;
}

/* 垂直排列的工具栏 */
.toolbar-vertical {
  top: 120px;
  right: 70px;
  flex-direction: column;
}

/* 水平排列的工具栏 */
.map-toolbar:not(.toolbar-vertical) {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: row;
}

.toolbar-group {
  display: flex;
  flex-direction: inherit;
}

.toolbar-group:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.toolbar-button {
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  color: #e3eafc;
  transition: all 0.3s;
}

.toolbar-vertical .toolbar-button {
  margin: 2px 0;
}

.map-toolbar:not(.toolbar-vertical) .toolbar-button {
  margin: 0 2px;
}

.toolbar-button:hover {
  background-color: #3a5eea;
  color: white;
}

.active-tool {
  background-color: #3a5eea;
  color: white;
}

.active-tool:hover {
  background-color: #5a73ea;
  color: white;
}

.layer-control-panel {
  position: absolute;
  top: 0;
  right: 45px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 10px;
  width: 150px;
}

.panel-title {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 10px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
}

.layer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.layer-item {
  font-size: 12px;
}

.no-layers {
  color: #909399;
  font-size: 12px;
  text-align: center;
  padding: 5px 0;
}

/* 定位对话框样式 */
.location-input-container {
  padding: 10px 0;
}

.coordinate-format-tip {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.dms-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dms-input-group .el-input {
  flex: 1;
}

.dms-input-group .el-select {
  flex-shrink: 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .map-toolbar {
    padding: 3px;
  }
  
  .toolbar-button {
    width: 28px;
    height: 28px;
  }
  
  .layer-control-panel {
    width: 120px;
    right: 40px;
  }
}
</style> 